<!-- Header -->
<header class="asset-app-header">
  <div class="logo-section">
    <button class="back-btn" (click)="goBack()">
      <app-icon name="go-back" size="16"></app-icon>
    </button>
    <div class="app-logo">StoryX</div>
    <div class="project-crumb">
      <span>{{ storyboardDetails?.video?.title }}</span> &gt;
      <span>{{ storyboardDetails?.title }}</span>
    </div>
  </div>
  <div class="header-actions">
    <div class="credit-pill">
      <span>Credits:</span>
      <strong>{{ credits }}</strong>
    </div>
    <button class="btn-ghost" (click)="share()">Share</button>
    <button class="btn-purple" (click)="export()">Export</button>
  </div>
</header>

<!-- Workspace -->
<div class="workspace">
  <!-- Left Panel: Scenes -->
  <aside class="panel-left" [class.collapsed]="isLeftPanelCollapsed">
    <div class="panel-header">
      <div class="panel-header-content">
        <div class="panel-header-title">Scenes</div>
      </div>
      <button
        class="panel-toggle-btn"
        (click)="toggleLeftPanel()"
        [title]="isLeftPanelCollapsed ? 'Expand panel' : 'Collapse panel'"
      >
        {{ isLeftPanelCollapsed ? "→" : "←" }}
      </button>
    </div>
    <div class="scene-list" [class.hidden]="isLeftPanelCollapsed">
      <div
        class="scene-item"
        [class.active]="selectedScene === 'all'"
        (click)="selectScene('all')"
      >
        <div class="scene-title">All Scenes</div>
        <div class="scene-meta">
          <span
            >{{ storyboardDetails?.shotlist?.scenes?.length || 0 }} Shots</span
          >
          <span>{{ totalDuration }} Sec</span>
        </div>
      </div>
      @for (scene of storyboardDetails?.shotlist?.scenes; track scene.id) {
        <div
          class="scene-item"
          [class.active]="selectedScene?.id === scene.id"
          (click)="selectScene(scene.id)"
        >
          <div class="scene-title">
            Scene {{ scene.scene_number }}: {{ scene.scene_title }}
          </div>
          <div class="scene-meta">
            <span
              >{{ scene?.shots?.length || 0 }}
              {{ scene?.shots?.length === 1 ? "Shot" : "Shots" }}</span
            >
            <span>{{ scene?.duration_seconds || 0 }} Sec</span>
          </div>
        </div>
      }
    </div>
  </aside>

  <!-- Center: Canvas -->
  <main class="canvas-area">
    <div class="d-flex align-items-center justify-content-center p-2">
      <div class="canvas-toolbar">
        <button
          class="view-toggle-btn"
          [class.active]="viewMode === 'grid'"
          (click)="switchView('grid')"
        >
          <span>▦</span> Grid
        </button>
        <button
          class="view-toggle-btn"
          [class.active]="viewMode === 'full'"
          (click)="switchView('full')"
        >
          <span>□</span> Full
        </button>
      </div>
    </div>

    <!-- Full View Container -->
    <div class="full-view-container" [class.active]="viewMode === 'full'">
      @if (getCurrentShots().length > 0) {
        <div class="nav-arrow nav-arrow-left" (click)="navigateShot(-1)">‹</div>
        <div class="full-shot-card">
          <img
            [src]="
              getCurrentShot()?.story_images?.[0]?.image
                ? getCurrentShot()?.story_images[0]?.image
                : '../../assets/images/storyboard_default_image.png'
            "
            class="full-shot-image"
            [alt]="getCurrentShot()?.description || 'Full shot'"
          />
          <div class="full-shot-info" style="padding: 1rem; text-align: center">
            <div style="font-weight: 600; margin-bottom: 0.5rem">
              Shot {{ getCurrentShot()?.shot_number }} of
              {{ getCurrentShots().length }}
            </div>
            <div style="font-size: 0.875rem; color: var(--muted-foreground)">
              {{ getCurrentShot()?.description }}
            </div>
          </div>
        </div>
        <div class="nav-arrow nav-arrow-right" (click)="navigateShot(1)">›</div>
      } @else {
        <div
          style="
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: var(--muted-foreground);
          "
        >
          <p>No shots available. Please select a scene.</p>
        </div>
      }
    </div>

    <!-- Grid View Container -->
    <div class="frames-container" [class.active]="viewMode === 'grid'">
      @if (
        !selectedScene ||
        !selectedScene.shots ||
        selectedScene.shots.length === 0
      ) {
        <div
          style="
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: var(--muted-foreground);
          "
        >
          <p>No shots available. Please select a scene.</p>
        </div>
      }
      @for (frame of selectedScene?.shots; track frame.id) {
        <div class="frame-card" (click)="selectShot(frame.id)">
          <div class="frame-visual">
            <button
              class="edit-btn-top-right btn btn-purple"
              (click)="editShot(frame.id); $event.stopPropagation()"
              title="Edit Shot"
            >
              Edit
            </button>
            <img
              [id]="'shot_images_' + frame.id"
              [src]="
                frame?.story_images[0]?.image
                  ? frame?.story_images[0]?.image
                  : '../../assets/images/storyboard_default_image.png'
              "
              [alt]="frame.description"
            />
            <div class="frame-overlay">
              <input
                type="file"
                [id]="'file-input-' + frame.id"
                accept="image/*"
                style="display: none"
                (change)="onFileSelected($event, frame.id)"
                (click)="$event.stopPropagation()"
              />
              <button
                class="btn-ghost"
                (click)="triggerFileInput(frame.id); $event.stopPropagation(); $event.preventDefault()"
                style="
                  background: rgba(0, 0, 0, 0.5);
                  color: white;
                  padding: 6px 12px;
                  font-size: 0.75rem;
                "
              >
                Upload
              </button>
              <button
                class="btn-ghost"
                (click)="
                  regenerate(
                    frame.id,
                    frame?.story_images[0]?.image ? 'Edit' : 'Create'
                  );
                  $event.stopPropagation()
                "
                style="
                  background: rgba(0, 0, 0, 0.5);
                  color: white;
                  padding: 6px 12px;
                  font-size: 0.75rem;
                "
              >
                {{ frame?.story_images[0]?.image ? "Regenerate" : "Generate" }}
              </button>
            </div>
          </div>
          <div class="frame-content">
            <div class="frame-header">
              <span class="frame-number">{{ frame.shot_number }}</span>
              <span style="font-size: 0.75rem; color: var(--muted-foreground)">
                {{ getSetupNames(frame.setups) }}
                <!-- • {{ frame.duration }} -->
              </span>
            </div>
            <div class="frame-desc">{{ frame.description }}</div>
            <div class="frame-tags">
              @for (entity of frame.entities; track tag) {
                <span class="tag">{{ entity?.entity?.name }}</span>
              }
            </div>
          </div>
        </div>
      }
    </div>
  </main>

  <!-- Right Panel: Details -->
  <aside class="panel-right">
    <div class="tabs">
      <div
        class="tab"
        [class.active]="activeTab === 'details'"
        (click)="switchTab('details')"
      >
        Details
      </div>
      <div
        class="tab"
        [class.active]="activeTab === 'script'"
        (click)="switchTab('script')"
      >
        Script
      </div>
    </div>
    <div class="panel-content">
      @if (activeTab === "details") {
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">Scene</span>
            <div class="detail-value">{{ selectedFrameDetails?.scene }}</div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Scene Description</span>
            <div
              class="detail-value"
              style="font-size: 0.8rem; line-height: 1.4"
            >
              {{ selectedFrameDetails?.sceneDescription ? selectedFrameDetails?.sceneDescription : "N/A" }}
            </div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Shot ID</span>
            <div class="detail-value">{{ selectedFrameDetails?.shotId }}</div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Shot Description</span>
            <div
              class="detail-value"
              style="font-size: 0.8rem; line-height: 1.4"
            >
              {{ selectedFrameDetails?.shotDescription }}
            </div>
          </div>
          <!-- <div class="info-row" style="margin-bottom: 0">
            <div class="detail-item" style="flex: 1">
              <span class="detail-label">Type</span>
              <div class="detail-value">{{ selectedFrameDetails?.type }}</div>
            </div>
            <div class="detail-item" style="flex: 1; margin-left: 12px">
              <span class="detail-label">Angle</span>
              <div class="detail-value">{{ selectedFrameDetails?.angle }}</div>
            </div>
          </div> -->
          <!-- <div class="detail-item">
            <span class="detail-label">Lens</span>
            <div class="detail-value">{{ selectedFrameDetails?.lens }}</div>
          </div> -->
          <div class="detail-item">
            <span class="detail-label">Entities</span>
            <div
              class="detail-value"
              style="background: transparent; border: none; padding: 0"
            >
              @for (entity of selectedFrameDetails?.entities; track entity) {
                <span class="entity-tag">{{ entity }}</span>
              }
            </div>
          </div>
        </div>
      }
      @if (activeTab === "script") {
        <div
          class="script-content"
          [innerHTML]="storyboardDetails?.shotlist?.draft?.content"
        ></div>
      }
    </div>
  </aside>
</div>
